import type { z } from 'zod';

import { type CodeDisplay, DEFAULT_CODE_DISPLAY } from './codeDisplay.js';
import type { NotesDatabase } from './db.js';
import { DEFAULT_EDITOR_MODE, type EditorMode, isEditorMode } from './editorMode.js';

/**
 * App settings. Small enough that a key/value table is the whole story; every
 * read returns a usable default so a fresh install needs no seeding.
 */

/**
 * A row of JSON, read whatever is in it. One written by a later version, or
 * hand-edited in devtools, is a value the app cannot use, and so the same as
 * none (`undefined`) rather than an error on the way into the app.
 */
export const readJson = <T>(value: string | undefined, schema: z.ZodType<T>): T | undefined => {
	if (value === undefined) return undefined;
	try {
		const parsed = schema.safeParse(JSON.parse(value));
		return parsed.success ? parsed.data : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Every pref kept for each source, by name. `sourceKey` takes nothing else, so
 * a pref added for a source is added here, and `store/sourcePrefs.ts`, which
 * forgets a source's prefs with it and carries them to it under a new id, is
 * made to say how it merges (#452).
 */
export const PREF_KEYS = [
	'lastOpen',
	'openNotebooks',
	'passwords',
	'pinnedNotebooks',
	'bottomPinnedNotebooks',
	'pinnedNotes',
	'scratchpad',
] as const;

export type SourcePref = (typeof PREF_KEYS)[number];

/** The key of a pref kept for each source: `<name>:<connectionId>`. */
export const sourceKey = (name: SourcePref, connectionId: string): string =>
	`${name}:${connectionId}`;

export const DEFAULT_EDITOR_MODE_KEY = 'defaultEditorMode';

export const getPreference = async (db: NotesDatabase, key: string): Promise<string | undefined> =>
	(await db.prefs.get(key))?.value;

export const setPreference = async (
	db: NotesDatabase,
	key: string,
	value: string
): Promise<void> => {
	await db.prefs.put({ key, value });
};

/** The mode a note opens in when it has no remembered mode of its own. */
export const getDefaultEditorMode = async (db: NotesDatabase): Promise<EditorMode> => {
	const stored = await getPreference(db, DEFAULT_EDITOR_MODE_KEY);
	return isEditorMode(stored) ? stored : DEFAULT_EDITOR_MODE;
};

export const setDefaultEditorMode = async (db: NotesDatabase, mode: EditorMode): Promise<void> => {
	await setPreference(db, DEFAULT_EDITOR_MODE_KEY, mode);
};

const FORMAT_TOOLBAR_KEY = 'formatToolbarShown';

/**
 * Whether a compact window shows the formatting toolbar under the note, on
 * this device, as the `Format` button last left it. Hidden unless it was
 * shown: it costs a row of a short screen, and the inline toolbar and the
 * slash menu are there without it.
 */
export const getFormatToolbarShown = async (db: NotesDatabase): Promise<boolean> =>
	(await getPreference(db, FORMAT_TOOLBAR_KEY)) === 'true';

export const setFormatToolbarShown = async (db: NotesDatabase, shown: boolean): Promise<void> => {
	await setPreference(db, FORMAT_TOOLBAR_KEY, String(shown));
};

const INSTALL_DISMISSED_KEY = 'installDismissed';

/**
 * Whether the banner offering to install the app has been dismissed on this
 * device (`install/InstallBanner.tsx`). Once is for good: it is not shown again
 * here. Kept as when, which nothing reads yet.
 */
export const getInstallDismissed = async (db: NotesDatabase): Promise<boolean> =>
	(await getPreference(db, INSTALL_DISMISSED_KEY)) !== undefined;

export const dismissInstall = (db: NotesDatabase, now: Date = new Date()): Promise<void> =>
	setPreference(db, INSTALL_DISMISSED_KEY, now.toISOString());

const CODE_BLOCK_WRAP_KEY = 'codeBlockWrap';
const CODE_BLOCK_LINE_NUMBERS_KEY = 'codeBlockLineNumbers';

/**
 * How code blocks are shown, for every note on this device.
 *
 * Two keys rather than one row of JSON, because they are two independent
 * answers and a half-written blob is a shape this table has no way to describe.
 * Anything that is not the string `true` is false, so a row from a future
 * version that means something else reads as the default rather than as an
 * error.
 */
export const getCodeDisplay = async (db: NotesDatabase): Promise<CodeDisplay> => {
	const [wrap, lineNumbers] = await Promise.all([
		getPreference(db, CODE_BLOCK_WRAP_KEY),
		getPreference(db, CODE_BLOCK_LINE_NUMBERS_KEY),
	]);

	return {
		wrap: wrap === undefined ? DEFAULT_CODE_DISPLAY.wrap : wrap === 'true',
		lineNumbers:
			lineNumbers === undefined ? DEFAULT_CODE_DISPLAY.lineNumbers : lineNumbers === 'true',
	};
};

export const setCodeDisplay = async (db: NotesDatabase, display: CodeDisplay): Promise<void> => {
	await Promise.all([
		setPreference(db, CODE_BLOCK_WRAP_KEY, String(display.wrap)),
		setPreference(db, CODE_BLOCK_LINE_NUMBERS_KEY, String(display.lineNumbers)),
	]);
};
